import { useState } from 'react';
import { Plus, Trash2, Copy } from 'lucide-react';
import { Button, Card, Input, Select } from '@dreamtree-org/twreact-ui';

/**
 * The agent editor's A2A tab: publish the agent as an A2A (Agent2Agent) server and author
 * the skills its Agent Card advertises. The card itself is built server-side
 * (services/a2a/agentCard.js), which also re-validates whatever is saved here, so this tab
 * only shapes input — it never decides what is safe to publish.
 */

const EXAMPLE_SEP = ' | ';

export function skillsFromJson(value) {
  try {
    const list = JSON.parse(value || '[]');
    return Array.isArray(list) ? list.map((s) => ({
      name: s.name || '',
      description: s.description || '',
      tags: (s.tags || []).join(', '),
      examples: (s.examples || []).join(EXAMPLE_SEP),
    })) : [];
  } catch {
    return [];
  }
}

export function skillsToJson(rows) {
  const list = rows
    .filter((r) => r.name.trim())
    .map((r) => ({
      name: r.name.trim(),
      description: r.description.trim() || r.name.trim(),
      tags: r.tags.split(',').map((t) => t.trim()).filter(Boolean),
      examples: r.examples.split('|').map((t) => t.trim()).filter(Boolean),
    }));
  return list.length ? JSON.stringify(list) : null;
}

function CopyLine({ label, value }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div className="space-y-1">
      <div className="text-xs font-medium text-secondary-600">{label}</div>
      <div className="flex items-start gap-2">
        <code className="flex-1 text-xs break-all rounded bg-secondary-50 dark:bg-secondary-900 px-2 py-1">{value}</code>
        <Button size="sm" variant="secondary" onClick={copy} aria-label={`Copy ${label}`}>
          <Copy size={12} className="mr-1 inline" /> {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </div>
  );
}

export default function AgentA2aTab({ form, setField, isNew }) {
  const skills = form.a2aSkills || [];
  const setSkill = (i, key, value) => setField('a2aSkills', skills.map((s, j) => (j === i ? { ...s, [key]: value } : s)));
  const addSkill = () => setField('a2aSkills', [...skills, { name: '', description: '', tags: '', examples: '' }]);
  const removeSkill = (i) => setField('a2aSkills', skills.filter((_, j) => j !== i));

  const base = typeof window !== 'undefined' ? window.location.origin : '';
  const agentUrl = `${base}/a2a/agents/${encodeURIComponent(form.key || '<key>')}`;
  const curl = [
    `curl -s ${agentUrl} \\`,
    '  -H "Authorization: Bearer tnt_…" -H "Content-Type: application/json" \\',
    `  -d '{"jsonrpc":"2.0","id":1,"method":"message/send","params":{"message":{"role":"user","messageId":"m-1","parts":[{"kind":"text","text":"Hello"}]}}}'`,
  ].join('\n');

  return (
    <div className="space-y-3">
      <Card className="p-3 space-y-2">
        <div className="text-sm font-semibold">A2A (Agent2Agent) — {form.a2a_enabled ? 'published' : 'not published'}</div>
        <Select
          label="Publish this agent over A2A"
          options={[
            { value: 'off', label: 'Off — no Agent Card, every A2A request gets 404' },
            { value: 'on', label: 'On — other agents can discover and call it with an agents:chat token' },
          ]}
          value={form.a2a_enabled ? 'on' : 'off'}
          onChange={(v) => setField('a2a_enabled', v === 'on')}
        />
        <p className="text-xs text-secondary-500">
          A2A callers run headless, like the public API: gated tools need this agent&apos;s Actions pre-approval,
          destructive and confirm tools never run, and ask_user becomes an input-required task the caller answers.
          The public card shows the name, description and skills below. It never shows the prompt, model or tools.
        </p>
      </Card>

      <Card className="p-3 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold">Skills — {skills.length || 'one general skill from the description'}</div>
          <Button size="sm" variant="secondary" onClick={addSkill}><Plus size={12} className="mr-1 inline" /> Add skill</Button>
        </div>
        {skills.map((s, i) => (
          <div key={i} className="grid gap-2 md:grid-cols-2 border-t border-secondary-200 dark:border-secondary-700 pt-3">
            <Input label="Name" value={s.name} onChange={(e) => setSkill(i, 'name', e.target.value)} placeholder="e.g. Refunds" />
            <Input label="Tags (comma-separated)" value={s.tags} onChange={(e) => setSkill(i, 'tags', e.target.value)} placeholder="billing, orders" />
            <Input label="Description" value={s.description} onChange={(e) => setSkill(i, 'description', e.target.value)} placeholder="What this skill does, for the calling agent" />
            <Input label="Examples (separate with |) — extended card only" value={s.examples} onChange={(e) => setSkill(i, 'examples', e.target.value)} placeholder="Refund order 1234 | Why was I charged twice?" />
            <div className="md:col-span-2 flex justify-end">
              <Button size="sm" variant="secondary" onClick={() => removeSkill(i)}><Trash2 size={12} className="mr-1 inline" /> Remove</Button>
            </div>
          </div>
        ))}
      </Card>

      {!isNew && (
        <Card className="p-3 space-y-3">
          <div className="text-sm font-semibold">Endpoints</div>
          <CopyLine label="Agent Card" value={`${agentUrl}/.well-known/agent-card.json`} />
          <CopyLine label="JSON-RPC endpoint (REST binding under /v1)" value={agentUrl} />
          <CopyLine label="Try it" value={curl} />
          {!form.a2a_enabled && <p className="text-xs text-amber-700">Not published yet. Turn publishing on and save, or these URLs return 404.</p>}
        </Card>
      )}
    </div>
  );
}
